import equal from "fast-deep-equal";
import linkifyHtml from "linkify-html";
import parser from "any-date-parser";
import { computeFormValidation } from "$src/lib/helpers/form-functions";
import {
  evaluate,
  evaluateNumeric,
  getFormulas,
  isNumeric,
} from "$lib/helpers/math";
import {
  getNonEmptyRows,
  hasFieldNoValue,
  isFieldEmpty,
  isValidDate,
  isValidUrl,
} from "$lib/helpers/validate";
import { BROWSER } from "esm-env";
import { getStripeCurrencies } from "$lib/data/constants";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

type TempFormState = {
  fields: string[];
  pages: string[];
  formMeta: FormMeta;
};

type TempFormStateParent = {
  fields: {
    id: string;
    valueComputed: any;
  }[];
  pages: string[];
  formMeta: FormMeta;
};

export function makeReadable(text: string) {
  if (!BROWSER) {
    return text;
  }
  // Function to convert ISO or yyyy-mm-dd dates to a readable format
  function convertToReadableDate(dateString, timeStyle) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleString(undefined, {
        dateStyle: "long",
        timeStyle: timeStyle,
      });
    }
    return dateString;
  }

  const element = document.createElement("div");
  element.innerHTML = text;
  const mentionNodes = element.querySelectorAll('[data-type="mention"]');

  mentionNodes.forEach((node) => {
    let textContent = node.textContent.trim();
    if (
      dayjs(textContent, "YYYY-MM-DD", true).isValid()
    ) {
      const readableDate = convertToReadableDate(
        dayjs(textContent).toISOString(),
        undefined,
      );
      textContent = readableDate;
    } else if (
      dayjs(textContent, [
        "YYYY-MM-DDTHH:mm:sss[Z]",
        "YYYY-MM-DDTHH:mm:ss.SSS[Z]",
        "YYYY-MM-DDTHH:mm:ss[Z]",
        "YYYY-MM-DDTHH:mm:ss.SSS[+0000]",
        "YYYY-MM-DDTHH:mm:ssZ",
      ], true).isValid()
    ) {
      const readableDate = convertToReadableDate(
        dayjs(textContent).toISOString(),
        "short",
      );
      textContent = readableDate;
    }
    node.textContent = textContent;
  });

  // Linkify all mentionNodes
  mentionNodes.forEach((node) => {
    const textContent = node.innerHTML.trim();
    const linkified = linkifyHtml(textContent, {
      defaultProtocol: "https",
      rel: "noopener noreferrer",
      target: "_blank",
    });
    node.innerHTML = linkified;
  });

  return element.innerHTML;
}

export function removeEmptyConditions(conditions: Conditions) {
  let conditionsCopy = clone(conditions);
  for (const conditionGroup of conditionsCopy.conditions) {
    for (const condition of conditionGroup.list) {
      if (
        condition?.field === "" &&
        condition?.match === ""
      ) {
        conditionGroup.list = conditionGroup.list.filter((x) =>
          x !== condition
        );
      }
    }
  }
  return conditionsCopy;
}

export function getGradientString(gradient: Gradient) {
  if (!gradient) return "white";
  if (gradient.type === "linear") {
    return `linear-gradient(${gradient.angle ?? 45}deg, ${
      gradient.computed.map((x) => `${x.color} ${x.position}%`).join(", ")
    })`;
  }
}

export function areBackgroundsEqual(
  one: BackgroundLayer[],
  two: BackgroundLayer[],
) {
  let oneLayers = [];
  let twoLayers = [];
  one.forEach((layer) => {
    if (layer.type) {
      oneLayers.push({
        ...layer[layer.type],
        opacity: layer.opacity,
      });
    }
  });
  two.forEach((layer) => {
    if (layer.type) {
      twoLayers.push({
        ...layer[layer.type],
        opacity: layer.opacity,
      });
    }
  });
  return equal(oneLayers, twoLayers);
}

export function sanitizeFilename(fileName: string) {
  const sanitized = fileName.replace(/[^a-zA-Z0-9-!_.*()''&$@=;/+ ]/g, "");
  return sanitized;
}

export function loadSVGElement() {
  if (BROWSER === false || customElements.get("svg-load")) return;
  customElements.define(
    "svg-load",
    class extends HTMLElement {
      async connectedCallback() {
        const src = this.getAttribute("src");
        const width = this.getAttribute("width");
        const height = this.getAttribute("height");
        const maxWidth = this.getAttribute("max-width");
        const maxHeight = this.getAttribute("max-height");
        const size = this.getAttribute("data-size");
        const shadowRoot = this.shadowRoot ||
          this.attachShadow({ mode: "open" });

        try {
          const fetchRequest = await fetch(src);
          if (!fetchRequest.ok) throw new Error("Fetch request failed");

          let svgContent = await fetchRequest.text();

          const parserObj = new DOMParser();
          const svgDoc = parserObj.parseFromString(svgContent, "image/svg+xml");
          const svgElement = svgDoc.querySelector("svg");

          if (size) {
            svgElement.setAttribute("width", size);
            svgElement.setAttribute("height", size);
          }
          if (width) {
            svgElement.setAttribute("width", width);
          } else {
            svgElement.setAttribute("width", "100%");
          }
          if (height) {
            svgElement.setAttribute("height", height);
          }
          if (maxHeight) {
            svgElement.style.maxHeight = maxHeight;
          }
          if (maxWidth) {
            svgElement.style.maxWidth = maxWidth;
          }
          svgContent = new XMLSerializer().serializeToString(svgElement);

          shadowRoot.innerHTML = svgContent;

          this.dispatchEvent(
            new CustomEvent("load", {
              bubbles: true,
              cancelable: false,
            }),
          );
        } catch (error) {
          shadowRoot.innerHTML = "×";
        }
      }
    },
  );
}

export function calculateFormProgress(
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  currentPageId: string,
) {
  const visiblePageIds = pageDisplay.filter(
    (page) => page.visibleComputed === true,
  ).map((page) => page.id);

  const visibleFields = fieldDisplay.filter(
    (field) =>
      field.visibleComputed === true && visiblePageIds.includes(field.page),
  );

  const required = visibleFields.filter(
    (field) =>
      field.type !== "RichText" && field.type !== "Hidden" &&
      field.type !== "CreditCard" && field.requiredComputed === true,
  );

  const filled = visibleFields.filter(
    (field) =>
      field.type !== "RichText" && field.type !== "Hidden" &&
      field.type !== "CreditCard" &&
      field.empty === false && field.requiredComputed === true,
  );

  return required.length === 0
    ? 100
    : Math.round((filled.length / required.length) * 100);
}

export function getRange(variants) {
  const numericValues = variants
    .filter((variant) => /^\d+$/.test(variant))
    .map(Number);
  if (numericValues.length === 0) {
    return "No numeric values found";
  }
  const min = Math.min(...numericValues);
  const max = Math.max(...numericValues);
  return `${min}..${max}`;
}

export function randomInt(min = 0, max = 100) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function clone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export function prepareVisit(visit: Visit, plan: PlanShort) {
  const visitCopy = clone(visit);
  if (plan.analytics_level < 2) {
    // Advanced analytics not supported. Mark nulls.
    visitCopy.fields = null;
    visitCopy.pages = null;
    visitCopy.last_field = null;
    visitCopy.last_page = null;
  } else {
    visitCopy.last_field = visitCopy.last_field == null
      ? "field0"
      : visitCopy.last_field;
    visitCopy.last_page = visitCopy.last_page == null
      ? "page0"
      : visitCopy.last_page;
  }
  return visitCopy;
}

export function getUTMParameters(url: string): Visit["tracking"] {
  const validParams = [
    "page_url",
    "utm_campaign",
    "utm_content",
    "utm_medium",
    "utm_source",
    "utm_term",
  ];
  const urlObj = new URL(url);
  const urlParams = new URLSearchParams(urlObj.search);
  const utmParams: Record<string, string> = {};

  for (const [key, value] of urlParams) {
    if (validParams.includes(key)) {
      utmParams[key] = value.split(",")[0].substring(0, 255);
    }
  }

  return utmParams;
}

export function formatBytes(
  bytes: number,
  unit: string = "Bytes",
  decimals: number = 2,
): string {
  if (bytes === 0 || bytes === null) {
    return "0 " + unit;
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  let i = sizes.indexOf(unit);
  if (i === -1) {
    return "Invalid unit";
  }

  bytes = bytes * Math.pow(k, i);
  i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

export function highlight(text: string, query: string) {
  const regexp = new RegExp(query, "gi");
  const match = text.match(regexp);
  return match ? text.replace(regexp, `<strong>${match[0]}</strong>`) : text;
}

export function toNumeric(num: string | number | undefined): number {
  if (typeof num === "number") {
    return num;
  }

  if (typeof num !== "string") {
    return 0;
  }

  return !isNaN(parseFloat(num)) ? parseFloat(num) : 0;
}

export const debounce = (fn: Function, ms = 300, callback: Function = null) => {
  if (!BROWSER) return fn;
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    if (callback) {
      callback(true);
    }
    timeoutId = setTimeout(() => {
      fn.apply(this, args);
      if (callback) {
        callback(false);
      }
    }, ms);
  };
};

export const updateFormValuesBasedOnLookup = async (
  formValues: FormValues,
  extraValues: ExtraValues,
  fields: Field[],
  lookup_id: string | null,
  locale: string = "en",
) => {
  let values: Record<string, string> = {};
  for (const field of fields) {
    if (field.type === "RichText") continue;
    if (field.type === "Calendly") continue;
    if (field.type === "Address") continue;
    if (field.type === "FileUpload") continue;
    if (field.type === "CreditCard") continue;
    if (field.type === "TableEntry") continue;
    if (field.type === "Hidden") continue;
    const formulas = getFormulas(field.default);
    if (formulas.length === 0) continue;
    const evaluated = await evaluate(
      field.default,
      extraValues,
      locale,
    );
    if (lookup_id === null) {
      values[field.id] = evaluated;
    } else if (field.default.includes(lookup_id)) {
      values[field.id] = evaluated;
    }
  }
  const newValues = applyFormValue(
    formValues,
    values,
    fields,
  );
  if (newValues) {
    return newValues;
  } else {
    return formValues;
  }
};

export const sanitizeReference = (reference: string) => {
  return reference.replace(/[^a-zA-Z0-9_.]/g, "").toLowerCase();
};

export const getValuesFromUrl = (url: string, fields: Field[] = []) => {
  try {
    const urlObject = new URL(url);
    const params = urlObject.searchParams;
    const values: Record<string, string | string[]> = {};
    const fieldLabels = fields.map((field) =>
      "label" in field ? field.label : null
    ).filter(Boolean);
    for (const key of params.keys()) {
      if (key.toLowerCase().startsWith("field")) {
        values[key.toLowerCase().replace("[]", "")] = params.getAll(key);
      } else if (fieldLabels.includes(key)) {
        const fieldId = fields.find((field) =>
          "label" in field && field.label === key
        )?.id;
        values[fieldId] = params.getAll(key);
      }
    }
    return values;
  } catch (error) {
    return {};
  }
};

export const applyFormValue = (
  formValues: FormValues,
  values: Record<string, string | string[]>,
  fields: Field[],
): FormValues | null => {
  const valuesCopy = clone(formValues);
  if (!values || Object.keys(values).length === 0) return null;
  for (const key of Object.keys(values)) {
    const field = findField(key, fields);
    if (!field) continue;
    if (field.type === "RichText") return;
    if (field.type === "Calendly") return;
    if (field.type === "Address") return;
    if (field.type === "FileUpload") return;
    if (field.type === "CreditCard") return;
    const value = values[key];
    const valueAsArray = Array.isArray(value) ? value : [value];
    const valueAsString = Array.isArray(value)
      ? value.join(",")
      : value === null
      ? ""
      : value;
    const valueAsNumber = parseFloat(valueAsString);
    if (field.type === "TableEntry") {
      let rows: TableEntryValue;
      rows = {
        headers: field.settings.columns.map((x) => x.label),
        rows: valueAsArray.map((row: string) => {
          return row.split(",").map((x) => x.trim()).splice(
            0,
            field.settings.columns.length,
          );
        }),
      };
      valuesCopy[field.id] = rows;
    } else if (
      field.type === "OneLineInput" || field.type === "Phone" ||
      field.type === "Comment" || field.type === "Email" ||
      field.type === "Hidden"
    ) {
      valuesCopy[field.id] = valueAsString;
    } else if (field.type === "Slider") {
      if (field.settings.type === "numbers") {
        const min = parseFloat(field.settings.min) ?? null;
        const max = parseFloat(field.settings.max) ?? null;
        if (min !== null && valueAsNumber < min) {
          continue;
        }
        if (max !== null && valueAsNumber > max) {
          continue;
        }
        valuesCopy[field.id] = {
          label: "",
          value: valueAsString,
        };
      } else if (field.settings.type === "labels") {
        const match = field.settings.options.find((option) => {
          return option.label === valueAsString ||
            option.value === valueAsString;
        });
        if (match) {
          valuesCopy[field.id] = {
            label: match.label,
            value: match.value,
          };
        }
      }
    } else if (field.type === "NPS") {
      if (valueAsNumber < 0 || valueAsNumber > 10) {
        continue;
      }
      valuesCopy[field.id] = parseInt(valueAsString).toString();
    } else if (field.type === "Rating") {
      if (valueAsNumber < 0 || valueAsNumber > 5) {
        continue;
      }
      valuesCopy[field.id] = parseInt(valueAsString).toString();
    } else if (field.type === "Date") {
      if (dayjs(valueAsString).isValid()) {
        valuesCopy[field.id] = dayjs(valueAsString).format("YYYY-MM-DD");
      }
    } else if (field.type === "Time") {
      if (dayjs(valueAsString, "HH:mm", false).isValid()) {
        valuesCopy[field.id] = dayjs(valueAsString, "HH:mm", false).format(
          "HH:mm",
        );
      }
    } else if (field.type === "MultipleChoice") {
      let foundOptions: ChoiceValue[] = [];
      if (field.options.source === "list") {
        foundOptions = field.options.list.filter((option) => {
          return valueAsArray.includes(option.value) || valueAsArray.includes(
            option.label,
          );
        });
      }
      let max = 1;
      if (field.settings.multiple) {
        max = field.settings.max ? parseInt(field.settings.max) : 100;
      }
      valuesCopy[field.id] = foundOptions.splice(0, max).map((
        { label, value },
      ) => ({
        label,
        value,
      }));
    } else if (field.type === "Dropdown") {
      let foundOptions: ChoiceValue[] = [];
      if (field.options.source === "list") {
        foundOptions = field.options.list.filter((option) => {
          return valueAsArray.includes(option.value) || valueAsArray.includes(
            option.label,
          );
        });
      }
      const max = field.settings.multiple && field.settings.max
        ? parseInt(field.settings.max)
        : 1;
      valuesCopy[field.id] = foundOptions.splice(0, max).map((
        { label, value },
      ) => ({
        label,
        value,
      }));
    }
  }
  return valuesCopy;
};

export const optionIsInList = (option: ChoiceOption, list: ChoiceValue[]) => {
  return (
    list.some(
      (x: ChoiceValue) => x.label === option.label,
    ) || false
  );
};

export const normalizeForComparison = (
  value:
    | string
    | ChoiceValue
    | ChoiceValue[]
    | Upload[]
    | CalendlyEvent
    | undefined,
  field = null,
) => {
  const format = "HH:mm";
  if (typeof value === "undefined") {
    return "";
  }
  const keyExtension = field ? field.replace(/^field\d+\./, "") : null;
  if (Array.isArray(value)) {
    if (typeof value[0] !== "undefined" && value[0].hasOwnProperty("key")) {
      return value.length;
    } else {
      value = (value as ChoiceValue[]).reduce((acc, curr) => {
        if (isNumeric(curr.value)) {
          return acc + parseFloat(curr.value.toString());
        }
        return acc;
      }, 0).toString();
    }
  } else if (typeof value !== "string" && "label" in value) {
    return value.value;
  } else if (typeof value === "object" && "full_address" in value) {
    if (keyExtension && keyExtension in value) {
      return value[keyExtension];
    } else {
      return value.full_address;
    }
  } else if (typeof value === "object" && "start_time" in value) {
    return convertISODateToDaysFromToday(value.start_time);
  } else if (typeof value === "string" && isNaN(Number(value)) === false) {
    return value;
  } else if (isValidDate(value)) {
    return convertISODateToDaysFromToday(value);
  } else if (
    dayjs(value, [
      "YYYY-MM-DDTHH:mm:sss[Z]",
      "YYYY-MM-DDTHH:mm:ss.SSS[Z]",
      "YYYY-MM-DDTHH:mm:ss[Z]",
      "YYYY-MM-DDTHH:mm:ss.SSS[+0000]",
      "YYYY-MM-DDTHH:mm:ssZ",
    ], true).isValid()
  ) {
    return convertISODateToDaysFromToday(value);
  } else if (dayjs(value, format, true).isValid()) {
    return convertTimeToMinutesFromNow(value);
  }
  return value;
};

function trim(str: string) {
  return str.replace(/^\s+|\s+$/gm, "");
}

const convertISODateToDaysFromToday = (isoDate: string) => {
  return (Math.ceil(dayjs().startOf("day").diff(dayjs(isoDate), "day", true)) *
    -1).toString();
};

// Convert HH:mm time to minutes from now
const convertTimeToMinutesFromNow = (time: string) => {
  const format = "HH:mm";
  if (!dayjs(time, format).isValid()) {
    return "0";
  }
  return (Math.ceil(
    dayjs().startOf("minute").diff(dayjs(time, format), "minute", true),
  ) * -1).toString();
};

export function scrollIntoViewIfNeeded(element, message: any) {
  const observerCallback = (entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        const coordinates = element.getBoundingClientRect();
        if (message) {
          message("scrollIntoView", coordinates);
        } else {
          element.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "nearest",
          });
        }
      }
      observer.unobserve(element);
    });
  };

  const observerOptions = {
    root: null,
    threshold: 1.0,
  };

  const observer = new IntersectionObserver(observerCallback, observerOptions);
  observer.observe(element);
}

// Function to check if element is in viewport
export function checkVisibility(element) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <=
      (window.innerWidth || document.documentElement.clientWidth)
  );
}

export function findFieldToScroll(addedNodes: Element[]): Element | null {
  if (!BROWSER) return addedNodes[0] as Element;

  let cumulativeHeight = 0;
  const viewportHeight = window.innerHeight;

  // Sort the fields based on their y coordinate
  const fields = addedNodes.sort((a, b) => {
    return a.getBoundingClientRect().top - b.getBoundingClientRect().top;
  });
  let previousField: Element | null = fields[0] ?? null;

  let i = 0;

  for (const node of fields) {
    const field = node as Element;
    const fieldHeight = field.getBoundingClientRect().height;
    const fieldMarginTop = parseInt(
      window.getComputedStyle(field).marginTop.replace("px", ""),
    );
    const fieldMarginBottom = parseInt(
      window.getComputedStyle(field).marginBottom.replace("px", ""),
    );
    cumulativeHeight += fieldHeight + fieldMarginTop + fieldMarginBottom;

    if (cumulativeHeight > viewportHeight) {
      return previousField;
    }

    previousField = field;
    i++;
  }
  if (previousField === null) {
    return null;
  }
  return checkVisibility(previousField) ? null : previousField;
}

/**
/**
 * @param { string } html The html to be parsed
 * @param { boolean } allow_line_breaks Whether to retain line breaks or not
 * @param { string[] } allowed_tags List of tags to be allowed (e.g., ['b', 'i'])
 * @returns { string } The parsed text with only allowed tags retained
 */
export function extractText(
  html: string,
  allow_line_breaks = false,
  allowed_tags: string[] = [],
): string {
  let textContent = "";
  let readingTag = false;
  let readingScriptOrStyle = false;
  let currentTag = "";
  let retainedContent = "";
  let insideCurlyBraces = false;

  if (!html) return "";

  // Convert <br> tags to new lines if line breaks are allowed
  if (allow_line_breaks) {
    html = html.replace(/<br\s*[/]?>/gi, "\n");
  }

  for (let i = 0; i < html.length; i++) {
    const char = html[i];

    if (char === "{") {
      insideCurlyBraces = true;
    }
    if (char === "}") {
      insideCurlyBraces = false;
    }

    if (!insideCurlyBraces) {
      if (char === "<") {
        readingTag = true;
        currentTag = "";
        retainedContent = "<";
        continue;
      }

      if (char === ">") {
        readingTag = false;
        retainedContent += ">";

        const tagName = currentTag.split(" ")[0]; // Extract tag name
        if (tagName === "script" || tagName === "style") {
          readingScriptOrStyle = true;
        } else if (tagName === "/script" || tagName === "/style") {
          readingScriptOrStyle = false;
        }

        if (
          !readingScriptOrStyle &&
          allowed_tags.includes(tagName.replace("/", "")) // Check if the tag (opening/closing) is allowed
        ) {
          textContent += retainedContent; // Retain the allowed tag
        }

        continue;
      }
    }

    if (readingTag) {
      currentTag += char.toLowerCase();
      retainedContent += char;
      continue;
    }

    if (readingScriptOrStyle) {
      continue;
    }

    textContent += char;
  }

  // Decode HTML entities
  textContent = textContent.replace(/&amp;/g, "&");
  textContent = textContent.replace(/&lt;/g, "<");
  textContent = textContent.replace(/&gt;/g, ">");

  // Remove line breaks if not allowed
  if (!allow_line_breaks) {
    textContent = textContent.replace(/(\r\n|\n|\r)/gm, "");
  }

  return textContent;
}

/**
 * @param {string} input The string to parse (separated by comma, semi-colon, or line break)
 * @return { Email[] } list of emails
 */
export const parseEmails = (input: string): Email[] => {
  const rawArray = input.split(/[\n,;]/).map((x) => x.trim());
  const regSimple =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const regComplex = /([^w]+)\s\<([\w@.]+)/;
  let results: Email[] = [];

  for (let rawItem of rawArray) {
    const findSimple = rawItem.match(regSimple);
    if (findSimple !== null) {
      results.push({
        name: "",
        email: findSimple[0],
      });
      continue;
    }
    const findComplex = rawItem.match(regComplex);
    if (findComplex !== null) {
      results.push({
        name: findComplex[1],
        email: findComplex[2],
      });
      continue;
    }
  }

  return results;
};

export function isEmptyObject(obj: any) {
  if (obj === null || obj === undefined) {
    return true;
  }

  if (typeof obj === "string") {
    return obj.length === 0 || obj === "<p></p>";
  }

  if (Array.isArray(obj)) {
    const arrayItems: any = obj.filter((item: any) => !isEmptyObject(item));
    return arrayItems.length === 0;
  }

  if (typeof obj === "object") {
    for (let key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        if (key === "id" || key === "operator") {
          continue;
        }
        if (!isEmptyObject(obj[key])) {
          return false;
        }
      }
    }
    return true;
  }

  return false;
}

export const filePath = (id: string) => {
  return `${import.meta.env.PUBLIC_APP_URL}/protected/dashboard/file?id=${id}`;
};

/**
 * @param { string } id The field id
 * @param { FieldDisplay[] } fieldDisplay The form state
 * @param { Logic } logic The $config.logic object
 * @param { FormValues } formValues The form values
 * @return { boolean } Whether the field is hidden
 */
export const isFieldHidden = async (
  id: string,
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  logic: Logic,
  formValues: FormValues,
  locale: string,
): Promise<boolean> => {
  for (let logicStep of logic) {
    if (!logicStep.actions) continue;
    for (let action of logicStep.actions) {
      if (!action.event) continue;
      if (action.event !== "fields") continue;
      if (!action.data || !action.data.fields) continue;
      const canFieldBeHidden = action.data.fields.includes(id);
      const conditionsFulfilled = await areConditionsFulfilled(
        fieldDisplay,
        pageDisplay,
        formValues,
        logicStep.conditions,
        locale,
      );
      if (canFieldBeHidden && !conditionsFulfilled) return true;
    }
  }
  return false;
};

function generateHTMLTable(data: TableEntryValue): string {
  // Filter out empty rows (where all values are empty strings)
  const nonEmptyRows = data.rows.filter((row) =>
    row.some((cell) => cell !== "")
  );

  if (nonEmptyRows.length === 0) return "";

  // Start table with styles
  let html =
    '<table style="width: 100%; border-collapse: collapse; font-size: 0.875rem">';

  // Add headers with styles
  if (data.headers.length > 0) {
    html += "<tr>";
    data.headers.forEach((header) => {
      html +=
        `<th style="text-align: left; padding: 0.4rem 0.6rem; border: 1px solid #ddd; font-weight: 600">${header}</th>`;
    });
    html += "  </tr>\n";
  }

  // Add rows with styles
  nonEmptyRows.forEach((row) => {
    html += "<tr>";
    row.forEach((cell) => {
      html +=
        `<td style="text-align: left; padding: 0.4rem 0.6rem; border: 1px solid #ddd;">${cell}</td>`;
    });
    html += "</tr>";
  });

  // Close table
  html += "</table>";

  return html;
}

/**
 * @param str String to strip references from
 * @param replace Optional replacement string
 * @returns Replaced string
 */
export const stripReferences = (str: string, replace: string = "@"): string => {
  return (str ?? "").replace(/\{\{(.*?)\}\}/g, "@");
};

export const prepareResponseSummary = (
  fields: Field[] | FieldDisplay[],
  pages: PageDisplay[] | Page[],
  frontEnd: boolean = false,
): string => {
  let responseArray: string[] = [];
  const hiddenPages = pages.filter((x: PageDisplay) =>
    x.visibleComputed === false
  ).map((x: PageDisplay) => x.id);
  for (const field of fields) {
    if ("visibleComputed" in field && field.visibleComputed === false) continue;
    if (hiddenPages.includes(field.page)) continue;

    if ("valueComputed" in field) {
      const fieldDisplay = field as FieldDisplay;
      if (fieldDisplay.type === "RichText") continue;
      const value = fieldDisplay.valueComputed;
      let displayString = "";

      if (hasFieldNoValue(value)) continue;
      if (frontEnd && field.type === "Hidden") continue;

      if (
        fieldDisplay.type === "MultipleChoice" ||
        fieldDisplay.type === "Dropdown"
      ) {
        displayString = (value as ChoiceValue[]).map((x: ChoiceValue) =>
          x.label
        ).join(", ");
      } else if (field.type === "FileUpload") {
        const fileLinks = (value as Upload[]).map((x: Upload) => {
          return `<a target='_blank' href='${filePath(x.id)}'>${x.name}</a>`;
        });
        displayString = fileLinks.join(", ");
      } else if (field.type === "TableEntry") {
        displayString = generateHTMLTable(value as TableEntryValue);
      } else if (field.type === "Calendly") {
        dayjs((value as CalendlyEvent).start_time).format("LLL");
      } else if (field.type === "Address") {
        displayString = value ? (value as Address).full_address : "";
      } else if (field.type === "CreditCard") {
        displayString = value as string;
      } else if (field.type === "Slider") {
        displayString = (value as ChoiceValue)?.label as string;
      } else if (field.type === "Comment") {
        const valueWithBreaks = (value as string).replace(/\n/g, "<br/>");
        displayString = valueWithBreaks;
      } else if (field.type === "Hidden") {
        const valueWithBreaks = (value as string).replace(/\n/g, "<br/>");
        displayString = valueWithBreaks;
      } else {
        displayString = value as string;
      }
      responseArray.push(
        `<strong>${fieldDisplay.label}</strong><br/>${displayString}`,
      );
    }
  }
  return responseArray.join("<br/><br/>");
};

const getGlobalValues = (
  result: any,
  formValues: FormValues,
  fields: FieldDisplay[] | Field[],
  pages: PageDisplay[] | Page[],
  metadata: Metadata,
  frontEnd: boolean = false,
  locale: string = "en",
) => {
  result["response_summary"] = prepareResponseSummary(
    fields,
    pages,
    frontEnd,
  );
  if (metadata.formLanguage) {
    result.form_language = metadata.formLanguage;
  }
  if (metadata.formId) {
    result.form_id = metadata.formId;
  }
  if (metadata.timeSpent) {
    result.time_spent = metadata.timeSpent.toString();
  }
  if (metadata.formName) {
    result.form_name = metadata.formName;
  }
  if (metadata.dateTime) {
    result.date_time = metadata.dateTime;
  }
  if (metadata.currentDate) {
    result.current_date = metadata.currentDate;
  }
  if (metadata.country) {
    result.country = metadata.country;
  }
  if (metadata.region) {
    result.region = metadata.region;
  }
  if (metadata.city) {
    result.city = metadata.city;
  }
  if (metadata.pageTitle) {
    result.page_title = metadata.pageTitle;
  }
  if (metadata.pageUrl) {
    result.page_url = metadata.pageUrl;
  }
  if (metadata.ipAddress) {
    result.ip_address = metadata.ipAddress;
  }
  if (metadata.stripe_pi_id) {
    result.stripe_pi_id = metadata.stripe_pi_id;
  }
  return result;
};

/**
 * Takes formValues and adds in hidden-type fields and global values
 * @param formValues Form values
 * @param fieldDisplay Field display
 * @param metadata Metadata
 * @param globals Whether to include global values
 * @param locale Locale
 * @returns Replaced string
 */
export const formValuesPlus = (
  formValues: FormValues,
  fieldDisplay: Field[],
  metadata: Metadata,
  globals = false,
  frontEnd = false,
  locale: string = "en",
): FormValues => {
  let result: FormValues = clone(formValues);
  for (let field of fieldDisplay) {
    if (field.type === "Hidden" || field.type === "CreditCard") {
      if (
        typeof formValues[field.id] !== "undefined" &&
        formValues[field.id] !== ""
      ) {
        result[field.id] = formValues[field.id];
      } else {
        result[field.id] = field.value;
      }
    }
  }
  if (globals) {
    result = getGlobalValues(
      result,
      formValues,
      fieldDisplay,
      [],
      metadata,
      frontEnd,
      locale,
    );
  }
  return result;
};

// formValues doesn't contain values from field type Hidden or CreditCard. Fix that. Remove values from hidden fields. Get globals.
export const formValuesPlusDisplay = (
  formValues: FormValues,
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  metadata: Metadata,
  workflowResults: WorkflowResult[] = [],
  frontEnd = false,
  locale: string = "en",
): FormValues => {
  const result: FormValues = clone(formValues);
  const hiddenPages = pageDisplay.filter((x: PageDisplay) =>
    x.visibleComputed === false
  ).map((x: PageDisplay) => x.id);
  for (let field of fieldDisplay) {
    const hidden = (typeof field.visibleComputed !== "undefined" &&
      field.visibleComputed === false) || hiddenPages.includes(field.page);
    if (
      hidden === false &&
      (field.type === "Hidden" || field.type === "CreditCard")
    ) {
      if (
        typeof formValues[field.id] !== "undefined" &&
        formValues[field.id] !== ""
      ) {
        result[field.id] = formValues[field.id];
      } else {
        const hasValue = field.valueComputed;
        if (hasValue) {
          // In some cases fieldDisplay will have values for hidden fields. Example in case of logic retries or setvalue logic.
          result[field.id] = field.valueComputed;
        } else {
          result[field.id] = field.value;
        }
      }
      if (
        typeof field.valueComputed !== "undefined" &&
        field.valueComputed &&
        field.valueComputed !== result[field.id] && metadata.timeSpent
      ) {
        result[field.id] = field.valueComputed;
      }
    } else if (hidden) {
      delete result[field.id];
    }
  }
  for (let workflow of workflowResults) {
    result[workflow.action] = workflow.data;
    if (workflow.other) {
      for (const [key, value] of Object.entries(workflow.other)) {
        result[`${workflow.action}_${key}`] = value;
      }
    }
  }
  return getGlobalValues(
    result,
    formValues,
    fieldDisplay,
    pageDisplay,
    metadata,
    frontEnd,
    locale,
  );
};

/**
 * @param { FieldDisplay[] }
 * @param { FormValues }
 * @param { LogicStep['conditions'] }
 * @returns { Boolean } Are conditions fulfilled
 */
export const areConditionsFulfilled = async (
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  formValues: FormValues,
  conditions: LogicStep["conditions"],
  locale = "en",
): Promise<Boolean> => {
  const conditionsToFulfill = conditions.operator === "or"
    ? Math.min(1, conditions.conditions.length)
    : conditions.conditions.length;
  let conditionsFulfilled = 0;

  const hiddenPages = pageDisplay.filter((x: PageDisplay) =>
    x.visibleComputed === false
  ).map((x: PageDisplay) => x.id);

  for (let condition of conditions.conditions) {
    const conditionsToFulfillInner = condition.operator === "or"
      ? 1
      : condition.list.length;
    let conditionsFulfilledInner = 0;

    for (let innerCondition of condition.list) {
      let field: any;

      if (!innerCondition.field.startsWith("field")) {
        const foundExtra = formValues[innerCondition.field] ??
          formValues[sanitizeReference(innerCondition.field)] ?? null;
        if (typeof foundExtra === "string") {
          field = {
            type: "OneLineInput",
            valueComputed: foundExtra as string,
          };
        } else {
          continue;
        }
      } else {
        const field_id_noex = innerCondition.field.replace(/\..+$/, "");
        const find = findField(field_id_noex, fieldDisplay);
        // If a field is hidden it can't act as a condition
        if (!find || !find.visibleComputed) continue;
        if (hiddenPages.includes(find.page)) continue;
        if (find.type === "RichText") continue;
        if (find.type === "TableEntry") continue;
        field = {
          type: find.type,
          valueComputed: find.valueComputed,
        };
      }

      const conditionMatchComputed = await evaluate(
        (extractText(innerCondition.match)).trim(),
        formValues,
        locale,
      );

      const numericEval = await evaluateNumeric(
        (extractText(innerCondition.match)).trim(),
        formValues,
        locale,
      );

      const valueComputedEvaluated = normalizeForComparison(
        field.valueComputed,
        innerCondition.field,
      );
      const conditionMatchComputedValue = normalizeForComparison(
        numericEval,
        innerCondition.field,
      );

      switch (innerCondition.operator) {
        case "is": {
          if (Array.isArray(field.valueComputed)) {
            const contains = field.valueComputed.some((x: any) =>
              x.label.trim() === conditionMatchComputed ||
              x.value.trim() === conditionMatchComputed
            );
            if (contains && field.valueComputed.length === 1) {
              conditionsFulfilledInner++;
            }
          } else {
            if (
              conditionMatchComputed === field.valueComputed ||
              conditionMatchComputedValue === valueComputedEvaluated
            ) {
              conditionsFulfilledInner++;
            }
          }
          break;
        }

        case "isnot": {
          const isEmptyLess = Array.isArray(field.valueComputed) &&
              field.valueComputed.length === 0 || field.valueComputed === "";
          // Removed Oct 21
          // if (isEmptyLess) break;
          if (Array.isArray(field.valueComputed)) {
            const contains = field.valueComputed.some((x: any) =>
              x.label.trim() === conditionMatchComputed ||
              x.value.trim() === conditionMatchComputed
            );
            if (!contains || field.valueComputed.length === 0) {
              conditionsFulfilledInner++;
            }
          } else {
            if (
              conditionMatchComputed !== field.valueComputed &&
              conditionMatchComputedValue !== valueComputedEvaluated
            ) {
              conditionsFulfilledInner++;
            }
          }
          break;
        }

        case "isempty": {
          if (Array.isArray(field.valueComputed)) {
            if (field.valueComputed.length === 0) conditionsFulfilledInner++;
          } else {
            if (
              typeof field.valueComputed === "undefined" ||
              field.valueComputed === ""
            ) {
              conditionsFulfilledInner++;
            }
          }
          break;
        }

        case "isnotempty": {
          if (Array.isArray(field.valueComputed)) {
            if (field.valueComputed.length > 0) conditionsFulfilledInner++;
          } else {
            if (
              typeof field.valueComputed !== "undefined" &&
              field.valueComputed !== ""
            ) {
              conditionsFulfilledInner++;
            }
          }
          break;
        }

        case "islessthan": {
          const resultLess = toNumeric(conditionMatchComputedValue);
          const floatComputedLess = toNumeric(valueComputedEvaluated);
          const isEmptyLess = Array.isArray(field.valueComputed) &&
              field.valueComputed.length === 0 || field.valueComputed === "";
          if (field.type === "Date" || field.type === "Time") {
            if (
              !isNaN(floatComputedLess) && floatComputedLess !== resultLess &&
              Math.min(floatComputedLess, resultLess) === floatComputedLess
            ) {
              conditionsFulfilledInner++;
            }
          } else {
            if (
              !isNaN(floatComputedLess) && floatComputedLess < resultLess &&
              isEmptyLess === false
            ) {
              conditionsFulfilledInner++;
            }
          }
          break;
        }

        case "ismorethan": {
          const resultMore = toNumeric(conditionMatchComputedValue);
          const floatComputedMore = toNumeric(valueComputedEvaluated);
          const isEmptyMore = Array.isArray(field.valueComputed) &&
              field.valueComputed.length === 0 || field.valueComputed === "";
          if (field.type === "Date" || field.type === "Time") {
            if (
              !isNaN(floatComputedMore) && floatComputedMore !== resultMore &&
              Math.min(floatComputedMore, resultMore) === resultMore
            ) {
              conditionsFulfilledInner++;
            }
          } else {
            if (
              !isNaN(floatComputedMore) && floatComputedMore > resultMore &&
              isEmptyMore === false
            ) {
              conditionsFulfilledInner++;
            }
          }
          break;
        }

        case "contains": {
          if (Array.isArray(field.valueComputed)) {
            const contains = field.valueComputed.some((x: any) =>
              x.label.toLowerCase().includes(
                conditionMatchComputed.toLowerCase(),
              ) ||
              x.value.toLowerCase().includes(
                conditionMatchComputed.toLowerCase(),
              )
            );
            if (contains) conditionsFulfilledInner++;
          } else if (
            typeof field.valueComputed !== "undefined" &&
            typeof field.valueComputed !== "string" &&
            "label" in field.valueComputed
          ) {
            if (
              field.valueComputed.label.toLowerCase().includes(
                conditionMatchComputed.toLowerCase(),
              )
            ) {
              conditionsFulfilledInner++;
            }
          } else if (
            typeof field.valueComputed !== "undefined" &&
            typeof field.valueComputed !== "string" &&
            "full_address" in field.valueComputed
          ) {
            if (
              field.valueComputed.full_address.toLowerCase().includes(
                conditionMatchComputed.toLowerCase(),
              )
            ) {
              conditionsFulfilledInner++;
            }
          } else if (
            field.valueComputed && typeof field.valueComputed === "string" &&
            field.valueComputed.toLowerCase().includes(
              conditionMatchComputed.toLowerCase(),
            )
          ) {
            conditionsFulfilledInner++;
          }
          break;
        }

        case "doesnotcontain": {
          if (Array.isArray(field.valueComputed)) {
            const contains = field.valueComputed.some((x: any) =>
              x.label === conditionMatchComputed ||
              x.value === conditionMatchComputed
            );
            if (!contains) conditionsFulfilledInner++;
          } else if (
            typeof field.valueComputed !== "string" &&
            "label" in field.valueComputed
          ) {
            // Slider field
            if (!field.valueComputed.label.includes(conditionMatchComputed)) {
              conditionsFulfilledInner++;
            }
          } else if (
            typeof field.valueComputed === "string" &&
            !field.valueComputed.includes(conditionMatchComputed)
          ) {
            conditionsFulfilledInner++;
          }
          break;
        }
      }
    }
    if (conditionsFulfilledInner >= conditionsToFulfillInner) {
      conditionsFulfilled++;
    }
  }
  return conditionsFulfilled >= conditionsToFulfill;
};

const applyActionsToFieldDisplay = async (
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  formValues: FormValues,
  formMeta: FormMeta,
  actions: LogicStep["actions"],
  locale: string = "en",
): Promise<
  {
    fieldDisplay: FieldDisplay[];
    pageDisplay: PageDisplay[];
    formMeta: FormMeta;
  }
> => {
  for (let action of actions) {
    switch (action.event) {
      case "fields": {
        if (!action.data.fields) continue;
        for (let showField of action.data.fields) {
          if (showField === "submit") {
            formMeta.submitButtonVisible = true;
          }
          const foundShow = findField(showField, fieldDisplay);
          if (foundShow) foundShow.visibleComputed = true;
        }
        break;
      }

      case "pages": {
        if (!action.data.pages) continue;
        for (let showPage of action.data.pages) {
          const foundShow = pageDisplay.find((x: PageDisplay) =>
            x.id === showPage
          );
          if (foundShow) foundShow.visibleComputed = true;
        }
        break;
      }

      case "setvalue": {
        if (typeof action.data.setValue?.field[0] === "undefined") continue;
        if (typeof action.data.setValue.value === "undefined") continue;
        const fieldVal = findField(action.data.setValue.field[0], fieldDisplay);
        if (!fieldVal) continue;
        if (fieldVal.type !== "Hidden") continue;
        const result = await evaluate(
          action.data.setValue.value,
          formValues,
          locale,
        );
        fieldVal.valueComputed = result;
        break;
      }

      case "markrequired": {
        if (!action.data.fields) continue;
        for (let showField of action.data.fields) {
          const foundShow = findField(showField, fieldDisplay);
          if (foundShow && "requiredComputed" in foundShow) {
            foundShow.requiredComputed = true;
          }
        }
        break;
      }

      case "setoptions": {
        if (
          !action.data.setOptions?.field[0] ||
          !action.data.setOptions?.options.list
        ) continue;
        const fieldOpt = findField(
          action.data.setOptions.field[0],
          fieldDisplay,
        );
        if (!fieldOpt) continue;
        if (
          fieldOpt.type !== "MultipleChoice" && fieldOpt.type !== "Dropdown"
        ) continue;
        fieldOpt.options.list = action.data.setOptions.options.list;
        if (fieldOpt.options.list) {
          fieldOpt.valueComputed = fieldOpt.valueComputed.filter((x) =>
            fieldOpt.options.list.find((y) => y.label === x.label)
          );
        }
        break;
      }

      case "disable": {
        formMeta.formDisabled = true;
        break;
      }
    }
  }
  return { fieldDisplay, pageDisplay, formMeta };
};

export const applyStripeDiscount = async (
  formStripe: FormStripe | undefined,
  amount: string,
) => {
  let result = {
    subTotal: amount,
    discount: "0",
    total: amount,
  };
  if (formStripe && formStripe.type === "charge" && isNumeric(amount)) {
    if (formStripe.coupon) {
      if (formStripe.coupon.type === "percent_off") {
        result.discount =
          (parseFloat(amount) * formStripe.coupon.discount / 100).toString();

        // Discount cannot make the total less than 0
        if (parseFloat(result.discount) > parseFloat(amount)) {
          result.discount = amount;
        }

        // Calculate total
        result.total = (parseFloat(amount) - parseFloat(result.discount))
          .toString();
      } else {
        const zeroCurrencies = getStripeCurrencies().zero;
        result.discount = zeroCurrencies.includes(formStripe.currency ?? "USD")
          ? formStripe.coupon.discount.toString()
          : (formStripe.coupon.discount / 100).toString();

        // Discount cannot make the total less than 0
        if (parseFloat(result.discount) > parseFloat(amount)) {
          result.discount = amount;
        }

        // Calculate total
        result.total = (parseFloat(amount) - parseFloat(result.discount))
          .toString();
      }
    }
  }
  return result;
};

export const computeFieldMap = async (
  fieldMap: FieldMap[],
  fieldDisplay: Field[],
  formValues: FormValues,
  metadata: Metadata,
  locale: string = "en",
) => {
  const formValuesCopy = formValuesPlus(
    formValues,
    fieldDisplay,
    metadata,
    false,
    false,
    locale,
  );
  let fieldMapCopy = clone(fieldMap) as FieldMap[];

  for (const row of fieldMapCopy) {
    row.fieldComputed = await evaluate(
      row.field,
      formValuesCopy,
      locale,
    );
  }

  return fieldMapCopy;
};

export const computeFormMeta = async (
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  config: Config,
  formMeta: FormMeta,
  formValues: FormValues,
  metadata: Metadata,
  locale: string = "en",
): Promise<FormMeta> => {
  const formValuesCopy = formValuesPlusDisplay(
    formValues,
    fieldDisplay,
    pageDisplay,
    metadata,
    [],
    false,
    locale,
  );
  formMeta.submitButtonText = await evaluate(
    config.submit?.text ?? "",
    formValuesCopy,
    locale,
  );
  formMeta.welcomePageContent = await evaluate(
    config.welcome?.content ?? "",
    formValuesCopy,
    locale,
  );
  formMeta.welcomePageButton = await evaluate(
    config.welcome?.button ?? "",
    formValuesCopy,
    locale,
  );
  return formMeta;
};

/**
 * @param fieldDisplay The form state
 * @param formValues The form values]
 * @param formStripe The form stripe object
 * @param locale The locale
 * @return { FieldDisplay[] } update state (only affects xComputed) for RichText fields
 */
export const computeRichTextAndStripe = async (
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  formValues: FormValues,
  formStripe: FormStripe | undefined,
  metadata: Metadata,
  frontEnd: boolean = false,
  locale: string = "en",
) => {
  let formValuesCopy: FormValues;

  formValuesCopy = formValuesPlusDisplay(
    formValues,
    fieldDisplay,
    pageDisplay,
    metadata,
    [],
    frontEnd,
    locale,
  );

  for (const field of fieldDisplay) {
    if (field.type === "RichText") {
      field.contentHTMLComputed = await evaluate(
        field.contentHTML,
        formValuesCopy,
        locale,
      );

      field.contentHTMLComputed = makeReadable(
        field.contentHTMLComputed,
      );

      // Replace line breaks with <br> tags
      field.contentHTMLComputed = field.contentHTMLComputed.replace(
        /(?:\r\n|\r|\n)/g,
        "<br>",
      );

      field.contentComputed = extractText(field.contentHTMLComputed, false, [
        "a",
        "br",
      ]);
    } else if (field.type === "CreditCard") {
      if (formStripe && formStripe.type === "charge") {
        const amount = await evaluateNumeric(
          field.value,
          formValuesCopy,
          locale,
        );
        ({
          subTotal: field["subTotalComputed"],
          discount: field["discountComputed"],
          total: field["valueComputed"],
        } = await applyStripeDiscount(formStripe, amount));
      } else {
        field["valueComputed"] = await evaluate(
          field.value,
          formValuesCopy,
          locale,
        );
        field["taxRatesComputed"] = await evaluate(
          field.taxRate ?? "",
          formValuesCopy,
          locale,
        );
        field["trialPeriodDaysComputed"] = await evaluate(
          field.settings.trialPeriodDays ?? "0",
          formValuesCopy,
          locale,
        );
      }
    }
  }
};

export const getImageSrc = (embed: Embed): string => {
  const supabaseUrl =
    `${import.meta.env.PUBLIC_SUPABASE_URL}/storage/v1/object/public/dashboard`;
  const appUrlBase = import.meta.env.PUBLIC_APP_URL;
  if (!embed) {
    return "";
  } else if (embed.source === "supabase") {
    const embedWithoutSlash = embed.content.startsWith("/")
      ? embed.content.slice(1)
      : embed.content;
    return encodeURI(`${supabaseUrl}/${embedWithoutSlash}`);
  } else if (embed.source === "app_url") {
    const embedWithoutSlash = embed.content.startsWith("/")
      ? encodeURI(embed.content.slice(1))
      : encodeURI(embed.content);
    return `${appUrlBase}/${embedWithoutSlash}`;
  } else if (embed.source === "website_url") {
    return encodeURI(
      `${import.meta.env.PUBLIC_SITE_URL}/images/${embed.content}`,
    );
  } else if (embed.source === "svg_url") {
    return encodeURI(
      `${import.meta.env.PUBLIC_APP_URL}/images/svg/${embed.content}`,
    );
  } else if (embed.source === "url") {
    return embed.content;
  } else {
    return embed.content;
  }
};

/**
 * @param fieldDisplay The form state
 * @param formValues The form values]
 * @param formStripe The form stripe object
 * @param locale The locale
 * @return { FieldDisplay[] } update state (only affects xComputed)
 */
export const computeFormValuesBasic = async (
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  config: Config,
  formValues: FormValues,
  formStripe: FormStripe | undefined,
  metadata: Metadata,
  locale: string = "en",
) => {
  let formValuesCopy: FormValues;

  formValuesCopy = formValuesPlus(
    formValues,
    fieldDisplay as FieldDisplay[],
    metadata,
    false,
    false,
    locale,
  );

  for (const field of fieldDisplay) {
    if (!field) continue;
    if (field.type === "RichText") continue;

    if (field.type === "Hidden") {
      if (formValuesCopy[field.id] && formValuesCopy[field.id] !== "") {
        field.valueComputed = formValuesCopy[field.id] as string;
        field.value = formValuesCopy[field.id] as string;
      }
      continue;
    } else if (field.type === "MultipleChoice" || field.type === "Dropdown") {
      field.valueComputed = (formValuesCopy[field.id] as ChoiceValue[]) ?? [];
    } else if (field.type === "FileUpload") {
      field.valueComputed = (formValuesCopy[field.id] as Upload[]) ?? [];
    } else if (field.type === "TableEntry") {
      field.valueComputed = (formValuesCopy[field.id] as TableEntryValue) ??
        { headers: [], rows: [] };
    } else if (field.type === "Slider") {
      field.valueComputed = (formValuesCopy[field.id] as ChoiceValue) ??
        undefined;
    } else if (field.type === "NPS") {
      field.valueComputed = (formValuesCopy[field.id] as string) ?? "";
    } else if (field.type === "Rating") {
      field.valueComputed = (formValuesCopy[field.id] as string) ?? "";
    } else if (field.type === "Calendly") {
      field.valueComputed = (formValuesCopy[field.id] as CalendlyEvent) ??
        undefined;
    } else if (field.type === "Address") {
      if (
        formValuesCopy[field.id] === null ||
        typeof formValuesCopy[field.id] === "undefined"
      ) {
        field.valueComputed = undefined;
      } else {
        const address = formValuesCopy[field.id] as Address;
        field.valueComputed = {
          gid: (address.gid ?? "") as string,
          full_address: (address.full_address ?? "") as string,
          line1: (address.line1 ?? "") as string,
          line2: (address.line2 ?? "") as string,
          city: (address.city ?? "") as string,
          state: (address.state ?? "") as string,
          postal_code: (address.postal_code ?? "") as string,
          country: (address.country ?? "") as string,
          geometry: address.geometry ?? null,
        };
      }
    } else {
      field.valueComputed = (formValuesCopy[field.id] as string) ?? "";
    }

    if (field.type === "Calendly") {
      const prefill_computed = await evaluate(
        `&name=${field.settings.namePrefill}&email=${field.settings.emailPrefill}`,
        formValuesCopy,
        locale,
      );
      if (prefill_computed !== field.settings.prefillComputed) {
        field.settings.prefillComputed = prefill_computed;
      }
    }

    if (field.type === "Time") {
      const minComputed = await evaluate(
        field.settings.min ?? "",
        formValuesCopy,
        locale,
      );
      if (
        minComputed &&
        dayjs(minComputed, "HH:mm", false).isValid()
      ) {
        field.settings.minComputed = dayjs(
          minComputed,
          "HH:mm",
          false,
        ).format("HH:mm");
      } else {
        field.settings.minComputed = "";
      }

      const maxComputed = await evaluate(
        field.settings.max ?? "",
        formValuesCopy,
        locale,
      );
      if (
        maxComputed &&
        dayjs(maxComputed, "HH:mm", false).isValid()
      ) {
        field.settings.maxComputed = dayjs(
          maxComputed,
          "HH:mm",
          false,
        ).format("HH:mm");
      } else {
        field.settings.maxComputed = "";
      }
    }

    if (field.type === "Date") {
      field.settings.minComputed = await evaluate(
        field.settings.min ?? "",
        formValuesCopy,
        locale,
      );
      if (isNumeric(field.settings.minComputed)) {
        field.settings.minComputed = dayjs().add(
          parseInt(field.settings.minComputed),
          "day",
        ).format("YYYY-MM-DD");
      } else if (dayjs(field.settings.minComputed).isValid()) {
        field.settings.minComputed = dayjs(field.settings.minComputed).format(
          "YYYY-MM-DD",
        );
      } else {
        field.settings.minComputed = dayjs().subtract(100, "year").format(
          "YYYY-MM-DD",
        );
      }

      field.settings.maxComputed = await evaluate(
        field.settings.max,
        formValuesCopy,
        locale,
      );
      if (isNumeric(field.settings.maxComputed)) {
        field.settings.maxComputed = dayjs().add(
          parseInt(field.settings.maxComputed),
          "day",
        ).format("YYYY-MM-DD");
      } else if (dayjs(field.settings.maxComputed).isValid()) {
        field.settings.maxComputed = dayjs(field.settings.maxComputed).format(
          "YYYY-MM-DD",
        );
      } else {
        field.settings.maxComputed = dayjs().add(100, "year").format(
          "YYYY-MM-DD",
        );
      }
    }

    if (field.type === "CreditCard") {
      if (formStripe && formStripe.type === "charge") {
        const amount = await evaluateNumeric(
          field.value,
          formValuesCopy,
          locale,
        );
        ({
          subTotal: field["subTotalComputed"],
          discount: field["discountComputed"],
          total: field["valueComputed"],
        } = await applyStripeDiscount(formStripe, amount));
      } else {
        field["valueComputed"] = await evaluate(
          field.value,
          formValuesCopy,
          locale,
        );
        field["taxRatesComputed"] = await evaluate(
          field.taxRate ?? "",
          formValuesCopy,
          locale,
        );
        field["trialPeriodDaysComputed"] = await evaluate(
          field.settings.trialPeriodDays ?? "0",
          formValuesCopy,
          locale,
        );
      }
    }

    if (field.type === "Dropdown") {
      const source = field.options.source;
      if (source === "list") {
        field.options.remoteString = "";
      } else if (source === "remoteUrl") {
        field.options.remoteString = field.options.remoteUrl?.url;
      } else if (source === "googleSheets") {
        field.options.remoteString = "";
      } else if (
        source === "salesforce" && field.options.salesforce?.type === "query"
      ) {
        field.options.remoteString = field.options.salesforce?.query;
      }
      if (field.options.remoteString) {
        field.options.remoteString = await evaluate(
          extractText(field.options.remoteString),
          formValuesCopy,
          locale,
        );
      }
    }

    field.labelComputed = await evaluate(
      field.label,
      formValuesCopy,
      locale,
    );

    field.labelHTMLComputed = await evaluate(
      field.labelHTML,
      formValuesCopy,
      locale,
    );

    field.descriptionHTMLComputed = await evaluate(
      field.descriptionHTML,
      formValuesCopy,
      locale,
    );
  }
};

/**
 * @param fieldDisplay The form state
 * @param formValues The form values]
 * @param formStripe The form stripe object
 * @param locale The locale
 * @return { FieldDisplay[] } update state (only affects xComputed)
 */
export const computeFormValuesHidden = async (
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  config: Config,
  formValues: FormValues,
  formStripe: FormStripe | undefined,
  metadata: Metadata,
  locale: string = "en",
) => {
  let formValuesCopy: FormValues;

  formValuesCopy = formValuesPlusDisplay(
    formValues,
    fieldDisplay as FieldDisplay[],
    pageDisplay as PageDisplay[],
    metadata,
    [],
    false,
    locale,
  );

  for (const lookup of config.lookups) {
    switch (lookup.source.type) {
      case "remoteUrl": {
        lookup.source.remoteUrl.url = await evaluate(
          extractText(lookup.source.remoteUrl.url),
          formValuesCopy,
          locale,
        );
        break;
      }
      case "salesforce": {
        if (lookup.source.salesforce.type === "query") {
          lookup.source.salesforce.query = await evaluate(
            extractText(lookup.source.salesforce.query),
            formValuesCopy,
            locale,
          );
        }
      }
    }
  }

  for (const field of fieldDisplay) {
    if (!field) continue;
    if (field.type !== "Hidden") continue;

    field.labelComputed = await evaluate(
      field.label,
      formValuesCopy,
      locale,
    );

    field.labelHTMLComputed = await evaluate(
      field.labelHTML,
      formValuesCopy,
      locale,
    );

    field.valueComputed = await evaluate(
      extractText(field.value, true, ["a"]),
      formValuesCopy,
      locale,
    );
  }
};

/**
 * @param fieldDisplay The form state
 * @param formValues The form values]
 * @param formStripe The form stripe object
 * @param locale The locale
 * @return { FieldDisplay[] } update state (only affects xComputed)
 */
export const computeOptionDescriptions = async (
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  config: Config,
  formValues: FormValues,
  formStripe: FormStripe | undefined,
  metadata: Metadata,
  locale: string = "en",
) => {
  let formValuesCopy: FormValues;

  formValuesCopy = formValuesPlusDisplay(
    formValues,
    fieldDisplay as FieldDisplay[],
    pageDisplay as PageDisplay[],
    metadata,
    [],
    false,
    locale,
  );

  for (const field of fieldDisplay) {
    if (field.type !== "MultipleChoice") continue;
    if (field.settings.descriptions === false) continue;
    if (field.options.source !== "list") continue;
    for (const item of field.options.list) {
      item.descriptionComputed = await evaluate(
        item.description,
        formValuesCopy,
        locale,
      );
    }
  }
};

/**
 * @param fieldDisplay The form state
 * @param formValues The form values]
 * @param formStripe The form stripe object
 * @param locale The locale
 * @return { FieldDisplay[] } update state (only affects xComputed)
 */
export const computeFieldLabels = async (
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  config: Config,
  formValues: FormValues,
  formStripe: FormStripe | undefined,
  metadata: Metadata,
  locale: string = "en",
) => {
  let formValuesCopy: FormValues;

  formValuesCopy = formValuesPlusDisplay(
    formValues,
    fieldDisplay as FieldDisplay[],
    pageDisplay as PageDisplay[],
    metadata,
    [],
    false,
    locale,
  );

  for (const field of fieldDisplay) {
    if (field.type === "RichText") continue;

    field.labelComputed = await evaluate(
      field.label,
      formValuesCopy,
      locale,
    );

    field.labelHTMLComputed = await evaluate(
      field.labelHTML,
      formValuesCopy,
      locale,
    );
  }
};

export const getHiddenFields = (fields: Field[], logic: Config["logic"]) => {
  let hidden = [];
  for (let logicStep of logic) {
    if (!logicStep.actions) continue;
    for (let action of logicStep.actions) {
      if (!action.event) continue;
      if (action.event !== "fields") continue;
      if (!action.data || !action.data.fields) continue;
      for (let field of action.data.fields) {
        let foundField = findField(field, fields);
        if (foundField) {
          hidden.push(foundField.id);
        }
      }
    }
  }
  return hidden;
};

/**
 * @param fieldDisplay The form state
 * @param logic The $config.logic object
 * @param formValues The form values
 * @return { fieldDisplay: FieldDisplay[], formMeta: FormMeta } updated state and meta
 */
export const computeFieldDisplay = async (
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  config: Config,
  formValues: FormValues,
  metadata: Metadata,
  locale: string = "en",
) => {
  let logic = config.logic || [];
  const fieldsMap = new Map(fieldDisplay.map((field) => [field.id, field]));

  let formMeta: FormMeta = {
    formDisabled: false,
    submitButtonVisible: true,
    submitButtonText: config.submit?.text ?? "",
    welcomePageContent: config.welcome?.content ?? "",
    welcomePageButton: config.welcome?.button ?? "",
  };

  for (let field of fieldDisplay) {
    field.visibleComputed = true;
  }
  for (let field of fieldDisplay) {
    if (field.type === "Hidden") continue;
    if (field.type === "RichText") continue;
    if (field.type === "CreditCard") continue;
    field.requiredComputed = field.settings.required;
  }
  for (let page of pageDisplay) {
    page.visibleComputed = true;
  }

  const skip = [];
  for (let logicStep of logic) {
    if (!logicStep.actions) continue;
    for (let action of logicStep.actions) {
      if (!action.event) continue;
      if (action.event !== "fields") continue;
      if (!action.data || !action.data.fields) continue;
      for (let field of action.data.fields) {
        if (field === "submit") {
          formMeta.submitButtonVisible = false;
        }
        if (skip.includes(field)) continue;
        let foundField = findFieldMap(field, fieldsMap);
        if (foundField) {
          foundField.visibleComputed = false;
        }
      }
    }
  }

  // Hide pages based on logic
  for (let logicStep of logic) {
    if (!logicStep.actions) continue;
    for (let action of logicStep.actions) {
      if (!action.event) continue;
      if (action.event !== "pages") continue;
      if (!action.data || !action.data.pages) continue;
      for (let page of action.data.pages) {
        let foundPage = pageDisplay.find((x: PageDisplay) => x.id === page);
        if (foundPage) foundPage.visibleComputed = false;
      }
    }
  }

  const formValuesCopy = formValuesPlusDisplay(
    formValues,
    fieldDisplay,
    pageDisplay,
    metadata,
    [],
    false,
    locale,
  );

  formMeta.submitButtonText = await evaluate(
    config.submit?.text ?? "",
    formValuesCopy,
    locale,
  );
  formMeta.welcomePageContent = await evaluate(
    config.welcome?.content ?? "",
    formValuesCopy,
    locale,
  );
  formMeta.welcomePageButton = await evaluate(
    config.welcome?.button ?? "",
    formValuesCopy,
    locale,
  );

  // Loop 5 times
  let oldState = {
    fields: [],
    pages: [],
    formMeta: {} as FormMeta,
  };
  for (let i = 0; i < 5; i++) {
    for (let logicStep of logic) {
      if (!logicStep.actions) continue;
      if (!logicStep.conditions) continue;
      let conditionsFulfilled = await areConditionsFulfilled(
        fieldDisplay,
        pageDisplay,
        formValuesCopy,
        logicStep.conditions,
        locale,
      );
      if (!conditionsFulfilled) continue;
      ({ fieldDisplay, pageDisplay, formMeta } =
        await applyActionsToFieldDisplay(
          fieldDisplay,
          pageDisplay,
          formValuesCopy,
          formMeta,
          logicStep.actions,
          locale,
        ));
    }
    const { isStateEqual, newState } = getIsStateEqual(
      fieldDisplay,
      pageDisplay,
      formMeta,
      oldState,
    );
    if (isStateEqual) {
      break;
    } else {
      oldState = newState;
    }
  }

  return formMeta;
};

function getIsStateEqualParent(
  fields: FieldDisplay[],
  pages: PageDisplay[],
  formMeta: FormMeta,
  oldState: TempFormStateParent,
) {
  const newState = {
    fields: fields.map((field) => {
      return {
        id: field.id,
        valueComputed: "valueComputed" in field ? field.valueComputed : "",
      };
    }),
    pages: pages.filter((x: PageDisplay) => x.visibleComputed === false).map((
      x: PageDisplay,
    ) => x.id),
    formMeta: clone(formMeta),
  };
  return {
    isStateEqual: equal(oldState, newState),
    newState,
  };
}

function getIsStateEqual(
  fields: FieldDisplay[],
  pages: PageDisplay[],
  formMeta: FormMeta,
  oldState: TempFormState,
) {
  let newState = {
    fields: fields.filter((x: FieldDisplay) => x.visibleComputed === false).map(
      (x: FieldDisplay) => x.id,
    ),
    pages: pages.filter((x: PageDisplay) => x.visibleComputed === false).map((
      x: PageDisplay,
    ) => x.id),
    formMeta: clone(formMeta),
  };
  return {
    isStateEqual: equal(oldState, newState),
    newState,
  };
}

/**
 * @param {id} id The field id
 * @param {fields} fields Collection of form fields
 * @return {field} A field or null
 */
export const findField = <T extends FieldDisplay[] | Field[]>(
  id: string,
  fields: T,
): T[number] | null => {
  if (!fields.length) return null;
  for (let field of fields) {
    if (field.id === id) return field as T[number];
  }
  return null;
};

/**
 * @param {id} id The field id
 * @param {fieldsMap} fieldsMap Map of form fields
 * @return {field} A field or null
 */
export const findFieldMap = (id, fieldsMap) => {
  return fieldsMap.get(id) || null;
};

/**
 * @param {fields} fields Collection of form fields
 * @return FormResponse object
 */
export const getFormResponseFromFields = (
  fields: FieldDisplay[],
  pages: PageDisplay[],
): FormResponseContent => {
  let formResponse: FormResponseContent = {};
  const hiddenPages = pages.filter((x: PageDisplay) =>
    x.visibleComputed === false
  ).map((x: PageDisplay) => x.id);
  for (let fieldIndex in fields) {
    const field = fields[fieldIndex];
    if (field.type === "RichText") continue;
    if (hiddenPages.includes(field.page)) continue;
    if (field.visibleComputed === false) continue;
    if (isFieldEmpty(field.valueComputed)) {
      formResponse[field.id] = {
        type: field.type,
        page: field.page,
        label: field.label,
      };
    } else if (field.type === "MultipleChoice" || field.type === "Dropdown") {
      formResponse[field.id] = {
        type: field.type,
        page: field.page,
        label: field.label,
        value: field.valueComputed,
      };
    } else if (field.type === "TableEntry") {
      formResponse[field.id] = {
        type: field.type,
        page: field.page,
        label: field.label,
        value: {
          headers: field.valueComputed.headers,
          rows: getNonEmptyRows(field.valueComputed.rows),
        },
      };
    } else if (field.type === "FileUpload") {
      formResponse[field.id] = {
        type: field.type,
        page: field.page,
        label: field.label,
        value: field.valueComputed,
      };
    } else if (field.type === "CreditCard") {
      formResponse[field.id] = {
        type: field.type,
        page: field.page,
        label: field.label,
        value: field.valueComputed,
        currency: field.settings.currency,
      };
    } else if (field.type === "Slider") {
      formResponse[field.id] = {
        type: field.type,
        page: field.page,
        label: field.label,
        value: field.valueComputed,
      };
    } else if (field.type === "Calendly") {
      formResponse[field.id] = {
        type: field.type,
        page: field.page,
        label: field.label,
        value: field.valueComputed,
      };
    } else if (field.type === "Address") {
      formResponse[field.id] = {
        type: field.type,
        page: field.page,
        label: field.label,
        value: field.valueComputed,
      };
    } else {
      formResponse[field.id] = {
        type: field.type,
        page: field.page,
        label: field.label,
        value: field.valueComputed,
      };
    }
  }
  return formResponse;
};

export const conditionsToSOQL = async (
  salesforceClient: any | null,
  json: Conditions,
  object: string,
  formValues: FormValues | null,
  type: "id" | "all" | string,
  limit = 0,
) => {
  let baseQuery = `SELECT ${
    type === "id" ? "id" : type === "all" ? "FIELDS(ALL)" : type
  } FROM ${object} WHERE `;

  async function buildConditionGroup(
    conditions: InnerCondition,
  ): Promise<string> {
    let group = "";

    for (const index in conditions.list) {
      const condition = conditions.list[index];
      let match;
      if (formValues !== null) {
        match = (await evaluate(condition.match, formValues, "en")).trim()
          .replace(/\\/g, "\\\\").replace(/'/g, "\\'");
      } else {
        match = extractText(condition.match);
      }
      if (condition.field === "related" && salesforceClient) {
        const { data: objects } = await salesforceClient.getObjects();
        for (const object_item of objects) {
          if (object_item.keyPrefix === match.substring(0, 3)) {
            const name = object_item.name;
            const get_more = await salesforceClient.request(
              `sobjects/${name}/${match}/${object}`,
              "GET",
            );
            if (get_more.data?.Id) {
              condition.field = "id";
              match = get_more.data.Id;
            }
          }
        }
      }
      const field_type = condition.meta?.meta?.type ?? "string";
      if (field_type === "date") {
        const parsedDate = parser.fromString(match);
        if (dayjs(parsedDate).isValid()) {
          match = dayjs(parsedDate).format("YYYY-MM-DD");
        }
      } else if (field_type === "datetime") {
        const parsedDate = parser.fromString(match);
        if (dayjs(parsedDate).isValid()) {
          match = dayjs(parsedDate).toISOString();
        }
      } else if (field_type === "boolean") {
        const falsyValues = ["false", "no", "0", "off", "null", "undefined"];
        const maybeFalse = falsyValues.includes(match.toLowerCase()) ||
          match === "";
        match = maybeFalse ? "false" : "true";
      }
      switch (condition.operator) {
        case "is":
        default:
          if (
            ["number", "double", "date", "datetime", "boolean"].includes(
              field_type,
            )
          ) {
            group += `${condition.field} = ${match}`;
          } else {
            group += `${condition.field} = '${match}'`;
          }
          break;
        case "isnot":
          if (
            ["number", "double", "date", "datetime", "boolean"].includes(
              field_type,
            )
          ) {
            group += `${condition.field} != ${match}`;
          } else {
            group += `${condition.field} != '${match}'`;
          }
          break;
        case "isempty":
          group += `${condition.field} = null`;
          break;
        case "isnotempty":
          group += `${condition.field} != null`;
          break;
        case "islessthan":
          group += `${condition.field} < ${match}`;
          break;
        case "ismorethan":
          group += `${condition.field} > ${match}`;
          break;
        case "contains":
          {
            match = match.replace(/%/g, "\\%").replace(/_/g, "\\_");
            group += `${condition.field} LIKE '%${match}%'`;
          }
          break;
      }
      if (parseInt(index) < conditions.list.length - 1) {
        group += ` ${conditions.operator.toUpperCase()} `;
      }
    }

    return conditions.list.length > 1 ? `(${group})` : group;
  }

  for (let index in json.conditions) {
    const conditionsGroup = json.conditions[index];
    baseQuery += await buildConditionGroup(conditionsGroup);
    if (parseInt(index) < json.conditions.length - 1) {
      baseQuery += ` ${json.operator.toUpperCase()} `;
    }
  }

  if (limit) {
    return baseQuery + ` LIMIT ${limit}`;
  }

  return baseQuery;
};

export const conditionsToQueryString = (json: Conditions) => {
  let query = "";
  for (let index in json.conditions) {
    const conditionsGroup = json.conditions[index];
    for (let indexInner in conditionsGroup.list) {
      const condition = conditionsGroup.list[indexInner];
      query += `${condition.field}_${condition.operator}_${condition.match}`;
      if (parseInt(indexInner) < conditionsGroup.list.length - 1) {
        query += `_${conditionsGroup.operator.toUpperCase()}_`;
      }
    }
    if (parseInt(index) < json.conditions.length - 1) {
      query += `_${json.operator.toUpperCase()}_`;
    }
  }
  return query;
};

export const hasLookupChanged = async (
  state: any = {},
  lookup: Lookup,
  fieldDisplay: FieldDisplay[],
  formValues: FormValues,
  extraValues: ExtraValues,
  metadata: Metadata,
  locale: string = "en",
) => {
  if (!lookup.source.type) {
    return { hasChanged: false, newState: null };
  }
  const formValuesCopy = formValuesPlus(
    formValues,
    fieldDisplay,
    metadata,
    true,
    false,
    locale,
  );
  const combinedValues = Object.assign({}, formValuesCopy, extraValues);
  switch (lookup.source.type) {
    case "remoteUrl": {
      state[lookup.id] = state[lookup.id] || {};
      let myState = state[lookup.id];
      const remoteUrl = lookup.source.remoteUrl;
      if (myState.lastLookup && myState.lastLookup === remoteUrl.url) {
        return { hasChanged: false, newState: null };
      }
      return { hasChanged: true, newState: remoteUrl };
      break;
    }
    case "zendesk": {
      state[lookup.id] = state[lookup.id] || {};
      let myState = state[lookup.id];
      let query = conditionsToQueryString(
        lookup.source.zendesk.objectConditions,
      );
      query = await evaluate(query, combinedValues, locale);
      if (query === "") {
        return { hasChanged: false, newState: null };
      }
      if (myState.lastLookup && myState.lastLookup === query) {
        return { hasChanged: false, newState: null };
      }
      return { hasChanged: true, newState: query };
      break;
    }
    case "hubspot": {
      state[lookup.id] = state[lookup.id] || {};
      let myState = state[lookup.id];
      let query = conditionsToQueryString(
        lookup.source.hubspot.objectConditions,
      );
      query = await evaluate(query, combinedValues, locale);
      if (query === "") {
        return { hasChanged: false, newState: null };
      }
      if (myState.lastLookup && myState.lastLookup === query) {
        return { hasChanged: false, newState: null };
      }
      return { hasChanged: true, newState: query };
      break;
    }
    case "salesforce": {
      state[lookup.id] = state[lookup.id] || {};
      let myState = state[lookup.id];
      let query;
      if (lookup.source.salesforce.type === "query") {
        query = lookup.source.salesforce.query;
      } else {
        query = await conditionsToSOQL(
          null,
          lookup.source.salesforce.objectConditions,
          lookup.source.salesforce.object?.value,
          combinedValues,
          "id",
          1,
        );
      }
      if (myState.lastLookup && myState.lastLookup === query) {
        return { hasChanged: false, newState: null };
      }
      return { hasChanged: true, newState: query };
      break;
    }
  }
  return { hasChanged: false, newState: null };
};

export const performLookup = (
  state: any = {},
  lookup: Lookup,
  fieldDisplay: FieldDisplay[],
  formValues: FormValues,
  extraValues: ExtraValues,
  metadata: Metadata,
  test: boolean,
  form_id: string,
  locale: string = "en",
) => {
  const formValuesCopy = formValuesPlus(
    formValues,
    fieldDisplay,
    metadata,
    true,
    false,
    locale,
  );
  const combinedValues = Object.assign({}, formValuesCopy, extraValues);

  if (!lookup.source.type) {
    return {
      state: state,
      changed: true,
      data: Promise.resolve({}),
    };
  }
  let result: Promise<any> | null = null;
  switch (lookup.source.type) {
    case "remoteUrl": {
      state[lookup.id] = state[lookup.id] || {};
      let myState = state[lookup.id];
      const remoteUrl = lookup.source.remoteUrl;
      if (myState.lastLookup && myState.lastLookup === remoteUrl.url) {
        return {
          state: state,
          changed: false,
          data: Promise.resolve({}),
        };
      }
      myState.lastLookup = remoteUrl.url;
      myState.abortController = new AbortController();
      result = fetch(
        "/public/form/lookup/remote-url",
        {
          method: "POST",
          body: JSON.stringify({
            test: test,
            form_id: form_id,
            lookup_id: lookup.id,
            values: combinedValues,
          }),
          signal: myState.abortController.signal,
        },
      ).then((response) => response.json()).catch(() => {
      });
      break;
    }
    case "zendesk": {
      state[lookup.id] = state[lookup.id] || {};
      let myState = state[lookup.id];
      myState.abortController = new AbortController();
      result = fetch("/public/form/lookup/zendesk", {
        method: "POST",
        body: JSON.stringify({
          test: test,
          form_id: form_id,
          lookup: lookup.id,
          values: combinedValues,
        }),
        signal: myState.abortController.signal,
      }).then((response) => response.json()).catch(() => {
      });
      break;
    }
    case "hubspot": {
      state[lookup.id] = state[lookup.id] || {};
      let myState = state[lookup.id];
      myState.abortController = new AbortController();
      result = fetch("/public/form/lookup/hubspot", {
        method: "POST",
        body: JSON.stringify({
          test: test,
          form_id: form_id,
          lookup: lookup.id,
          values: combinedValues,
        }),
        signal: myState.abortController.signal,
      }).then((response) => response.json()).catch(() => {
      });
      break;
    }
    case "salesforce": {
      state[lookup.id] = state[lookup.id] || {};
      let myState = state[lookup.id];
      myState.abortController = new AbortController();
      result = fetch("/public/form/lookup/salesforce", {
        method: "POST",
        body: JSON.stringify({
          test: test,
          form_id: form_id,
          lookup: lookup.id,
          values: combinedValues,
        }),
        signal: myState.abortController.signal,
      }).then((response) => response.json()).catch(() => {
      });
      break;
    }
  }
  return {
    state: state,
    changed: true,
    data: result,
  };
};

export const computeFormState = async (
  fields: Field[],
  pages: Page[],
  config: Config,
  formValues: FormValues,
  extraValues: ExtraValues,
  formStripe: FormStripe | undefined,
  metadata: Metadata,
  frontEnd: boolean = false,
  locale: string = "en",
) => {
  let formMeta: FormMeta = {} as FormMeta;
  let fieldDisplay = clone(fields as FieldDisplay[]);
  let pageDisplay = clone(pages as PageDisplay[]);
  let configDisplay = clone(config);

  let formValuesCopy = Object.assign({}, formValues, extraValues);
  let formStripeCopy = Object.assign({}, formStripe);

  Object.freeze(formValuesCopy);
  Object.freeze(formStripeCopy);

  // Updates fieldDisplay
  await computeFormValuesBasic(
    fieldDisplay,
    pageDisplay,
    configDisplay,
    formValuesCopy,
    formStripeCopy,
    metadata,
    locale,
  );

  let oldState: TempFormStateParent = {
    fields: [],
    pages: [],
    formMeta: {} as FormMeta,
  };
  for (let i = 0; i < 5; i++) {
    // Updates fieldDisplay
    await computeFormValuesHidden(
      fieldDisplay,
      pageDisplay,
      configDisplay,
      formValuesCopy,
      formStripeCopy,
      metadata,
      locale,
    );

    // Updates fieldDisplay, pageDisplay, formMeta
    formMeta = await computeFieldDisplay(
      fieldDisplay,
      pageDisplay,
      configDisplay,
      formValuesCopy,
      metadata,
    );

    const { isStateEqual, newState } = getIsStateEqualParent(
      fieldDisplay,
      pageDisplay,
      formMeta,
      oldState,
    );
    if (isStateEqual) {
      break;
    } else {
      oldState = newState;
    }
  }

  // Update formMeta (only some fields)
  formMeta = await computeFormMeta(
    fieldDisplay,
    pageDisplay,
    configDisplay,
    formMeta,
    formValuesCopy,
    metadata,
  );

  // Updates richText fields
  await computeRichTextAndStripe(
    fieldDisplay,
    pageDisplay,
    formValuesCopy,
    formStripeCopy,
    metadata,
    frontEnd,
    locale,
  );

  // Update fieldLabel
  await computeFieldLabels(
    fieldDisplay,
    pageDisplay,
    configDisplay,
    formValuesCopy,
    formStripeCopy,
    metadata,
    locale,
  );

  // Update fieldLabel
  await computeOptionDescriptions(
    fieldDisplay,
    pageDisplay,
    configDisplay,
    formValuesCopy,
    formStripeCopy,
    metadata,
    locale,
  );

  await computeFormValidation(
    fieldDisplay,
    pageDisplay,
    formValuesCopy,
    formMeta,
  );

  const readOnlyValues = await formValuesPlusDisplay(
    formValuesCopy,
    fieldDisplay,
    pageDisplay,
    metadata,
    [],
    frontEnd,
    locale,
  );

  return {
    fieldDisplay,
    pageDisplay,
    configDisplay,
    formMeta,
    readOnlyValues,
  };
};
